import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { useEffect, useState, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, MapPin, Flag, Trash2, Loader2, Check, Footprints } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

const haversineM = (lat1, lng1, lat2, lng2) => {
  const R = 6371000, rad = (d) => (d * Math.PI) / 180;
  const dp = rad(lat2 - lat1), dl = rad(lng2 - lng1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const personIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#0020C2;width:12px;height:12px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.4);"></div>`,
  iconSize: [12, 12], iconAnchor: [6, 6],
});

const stopIcon = (n) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#0A0A0A;color:#fff;width:26px;height:26px;border-radius:50%;border:2.5px solid white;box-shadow:0 1px 5px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;font-family:monospace;cursor:grab;">${n}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

const destIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#DC2626;color:#fff;padding:3px 8px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);font-size:10px;font-weight:800;letter-spacing:1px;white-space:nowrap;">HEDEF</div>`,
  iconSize: [52, 22], iconAnchor: [26, 11],
});

const ClickCatcher = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
};

const FitOnce = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    else if (points.length === 1) map.setView(points[0], 14);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
};

// Manuel güzergah için haritada durak + hedef işaretleme.
// Personel EN YAKIN durağa atanır; yürüme mesafesi durak-adres arası hesaplanır.
export const StopBuilderMapModal = ({ people, initialStops = [], initialDest = null, onApply, onClose }) => {
  const located = useMemo(() => people.filter((p) => typeof p.lat === "number" && typeof p.lng === "number"), [people]);
  const unlocatedCount = people.length - located.length;
  const [stops, setStops] = useState(initialStops.map((s) => ({ lat: s.lat, lng: s.lng })));
  const [dest, setDest] = useState(initialDest ? { lat: initialDest.lat, lng: initialDest.lng, label: initialDest.label || "" } : null);
  const [mode, setMode] = useState("stop"); // stop | dest
  const [busy, setBusy] = useState(false);

  // Canlı atama: her personel en yakın durağa
  const assignment = useMemo(() => located.map((p) => {
    if (stops.length === 0) return null;
    let best = 0, bd = Infinity;
    stops.forEach((s, i) => {
      const d = haversineM(p.lat, p.lng, s.lat, s.lng);
      if (d < bd) { bd = d; best = i; }
    });
    return { stopIdx: best, walk: Math.round(bd) };
  }), [located, stops]);

  const stopCounts = useMemo(() => {
    const c = stops.map(() => 0);
    assignment.forEach((a) => { if (a) c[a.stopIdx] += 1; });
    return c;
  }, [stops, assignment]);

  const handleMapClick = (latlng) => {
    if (mode === "dest") setDest({ lat: latlng.lat, lng: latlng.lng, label: "" });
    else setStops((s) => [...s, { lat: latlng.lat, lng: latlng.lng }]);
  };

  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));

  const apply = async () => {
    if (stops.length === 0) { toast.error("Haritaya tıklayarak en az bir durak ekleyin"); return; }
    if (!dest) { toast.error("'Hedef Seç' moduna geçip varış noktasını işaretleyin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/transport-routes/build-map", {
        personnel_ids: people.map((p) => p.id),
        stops: stops.map(({ lat, lng }) => ({ lat, lng })),
        dest,
      });
      if (data.unlocated?.length > 0) toast.warning(`${data.unlocated.length} personel konumsuz olduğu için durağa atanamadı: ${data.unlocated.slice(0, 3).join(", ")}${data.unlocated.length > 3 ? "..." : ""}`, { duration: 7000 });
      onApply(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const fitPoints = located.map((p) => [p.lat, p.lng]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[94vh] overflow-y-auto" data-testid="stopbuilder-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-[600]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Haritada Güzergah Oluştur</div>
            <div className="text-xs text-neutral-600 mt-0.5">
              <b className="num">{located.length}</b> konumlu personel{unlocatedCount > 0 && <span className="text-[#B45309]"> · {unlocatedCount} konumsuz (atlanır)</span>} — haritaya tıklayarak durak ekleyin, personeller en yakın durağa atanır.
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="stopbuilder-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Mod seçimi */}
        <div className="px-6 py-3 border-b border-[#F5F5F5] flex items-center gap-2 flex-wrap">
          <button
            data-testid="stopbuilder-mode-stop"
            onClick={() => setMode("stop")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${mode === "stop" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
          >
            <MapPin className="w-3.5 h-3.5" /> Durak Ekle
          </button>
          <button
            data-testid="stopbuilder-mode-dest"
            onClick={() => setMode("dest")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${mode === "dest" ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
          >
            <Flag className="w-3.5 h-3.5" /> Hedef Seç {dest ? "✓" : ""}
          </button>
          {stops.length > 0 && (
            <button data-testid="stopbuilder-clear" onClick={() => setStops([])} className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-neutral-500 hover:text-[#DC2626]">
              <Trash2 className="w-3 h-3" /> Durakları Temizle
            </button>
          )}
          <span className="ml-auto text-xs text-neutral-500"><b className="num">{stops.length}</b> durak · durağa tıklayıp silebilir, sürükleyerek taşıyabilirsiniz</span>
        </div>

        <div style={{ height: 440 }}>
          <MapContainer center={fitPoints[0] || [40.9, 29.3]} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitOnce points={fitPoints} />
            <ClickCatcher onClick={handleMapClick} />
            {/* Personel adres noktaları */}
            {located.map((p, i) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={personIcon}>
                <Tooltip direction="top" offset={[0, -6]}>
                  <span style={{ fontSize: 11, fontWeight: 700 }}>{p.first_name} {p.last_name}</span>
                  {assignment[i] && <span style={{ fontSize: 10 }}> — Durak {assignment[i].stopIdx + 1}&apos;e {assignment[i].walk} m</span>}
                </Tooltip>
              </Marker>
            ))}
            {/* Personel → durak bağlantı çizgileri */}
            {located.map((p, i) => assignment[i] && (
              <Polyline
                key={`ln-${p.id}`}
                positions={[[p.lat, p.lng], [stops[assignment[i].stopIdx].lat, stops[assignment[i].stopIdx].lng]]}
                pathOptions={{ color: "#0020C2", weight: 1.5, opacity: 0.45, dashArray: "4 4" }}
              />
            ))}
            {/* Duraklar (sürüklenebilir, tıkla-sil) */}
            {stops.map((s, i) => (
              <Marker
                key={`stop-${i}`}
                position={[s.lat, s.lng]}
                icon={stopIcon(i + 1)}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const ll = e.target.getLatLng();
                    setStops((arr) => arr.map((x, idx) => (idx === i ? { lat: ll.lat, lng: ll.lng } : x)));
                  },
                  click: () => { if (window.confirm(`Durak ${i + 1} silinsin mi?`)) removeStop(i); },
                }}
              >
                <Tooltip direction="top" offset={[0, -14]} permanent>
                  <span style={{ fontSize: 10, fontWeight: 800 }}>{stopCounts[i]} kişi</span>
                </Tooltip>
              </Marker>
            ))}
            {dest && <Marker position={[dest.lat, dest.lng]} icon={destIcon} draggable eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setDest((d) => ({ ...d, lat: ll.lat, lng: ll.lng })); } }} />}
          </MapContainer>
        </div>

        {/* Durak bazlı yürüme mesafeleri */}
        {stops.length > 0 && (
          <div className="px-6 py-3 border-t border-[#F5F5F5] flex flex-wrap gap-2 text-xs">
            {stops.map((s, i) => {
              const ppl = located.filter((_, pi) => assignment[pi]?.stopIdx === i);
              return (
                <div key={i} className="border border-[#E5E5E5] px-2.5 py-1.5" data-testid={`stopbuilder-stop-${i}`}>
                  <span className="font-bold">Durak {i + 1}</span> · {ppl.length} kişi
                  {ppl.length > 0 && (
                    <span className="text-neutral-500 flex items-center gap-1 mt-0.5">
                      <Footprints className="w-3 h-3" />
                      maks yürüme <b className="num">{Math.max(...ppl.map((_, idx) => {
                        const gi = located.indexOf(ppl[idx]);
                        return assignment[gi]?.walk || 0;
                      }))} m</b>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E5E5E5]">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
          <button data-testid="stopbuilder-apply" onClick={apply} disabled={busy} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {busy ? "Hesaplanıyor..." : "Hesapla ve Uygula"}
          </button>
        </div>
      </div>
    </div>
  );
};
